import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import { BarcodeScanner, ScanButton } from '../../Common';
import { isBarcodeScanSupported } from '../../../utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { chromeInfoBackground } from '../../../color_utils';

const SEARCH_FIELDS = ['tagNumber', 'serialNumber'];

// The Unknown cylinder (id 0) is a sentinel with no tag — a scan must never resolve to it.
const isScannable = (cylinder) => cylinder && cylinder.id !== 0;

// Exact match first (a full barcode resolves to one cylinder), then case-insensitive Contains.
const matchCylinders = (cylinders, value) => {
    const v = value.trim().toLowerCase();
    if (!v) return [];
    const eligible = cylinders.filter(isScannable);
    const exact = eligible.filter(c =>
        SEARCH_FIELDS.some(f => c[f] && String(c[f]).trim().toLowerCase() === v)
    );
    if (exact.length > 0) return exact;
    return eligible.filter(c =>
        SEARCH_FIELDS.some(f => c[f] && String(c[f]).toLowerCase().includes(v))
    );
};

const CylinderScanLookup = ({ cylinders, onSelect, onSearchValue, serviceEventType, disabled }) => {
    const [open, setOpen] = useState(false);
    const scanLabel = TranslateW('ScanBarcode');
    const scanTitle = TranslateW('FexaTrakref');

    if (!isBarcodeScanSupported() || !(isMobile || isTablet)) return null;

    const close = () => {
        setOpen(false);
    };

    const select = (cylinderId) => {
        onSelect(cylinderId);
        close();
    };

    // One match selects it; zero or many hand the value to the dropdown to filter or show empty state.
    const handleDecode = (value) => {
        const found = matchCylinders(cylinders, value);
        if (found.length === 1) {
            select(found[0].id);
            return;
        }
        close();
        onSearchValue(value);
    };

    return (
        <>
            <ScanButton
                onClick={() => setOpen(true)}
                disabled={disabled}
                tooltipText={scanLabel}
                ariaLabel={scanLabel}
                size="small"
                iconSize={18}
                badgeSx={{ '& .MuiBadge-badge': { fontSize: '8px', height: 14, minWidth: 26, top: 0, right: 0 } }}
                sx={{
                    backgroundColor: chromeInfoBackground,
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: chromeInfoBackground },
                }}
            />
            <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(0,0,0,0.23)', mx: 0.5 }} />
            {open && (
                <BarcodeScanner
                    open={open}
                    onClose={close}
                    onDecode={handleDecode}
                    title={scanTitle}
                    source="service-event-cylinder-scan"
                    sourceContext={serviceEventType}
                />
            )}
        </>
    );
};

CylinderScanLookup.propTypes = {
    cylinders: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
    onSearchValue: PropTypes.func.isRequired,
    serviceEventType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
};

CylinderScanLookup.defaultProps = {
    cylinders: [],
    serviceEventType: undefined,
    disabled: false,
};

export default CylinderScanLookup;
